import {
  type ClearPersonalComposer,
  type PersonalComposerRead,
  PersonalComposerReadSchema,
  type PersonalComposerWrite,
  PersonalComposerWriteSchema,
  type SaveComposerFull,
} from "../../src/personal-composer-contract.ts";

export class ComposerRequestError extends Error {
  constructor(readonly code: string) {
    super(code);
  }
}

export type PersonalComposerClient = {
  read(): Promise<PersonalComposerRead>;
  save(input: SaveComposerFull): Promise<PersonalComposerWrite>;
  clear(input: ClearPersonalComposer): Promise<PersonalComposerWrite>;
};

async function responseBody(response: Response): Promise<unknown> {
  const body: unknown = await response.json();
  if (response.ok) return body;
  const code =
    typeof body === "object" && body !== null && "error" in body && typeof body.error === "string"
      ? body.error
      : "request_failed";
  // A generic 404 is a failed read. Only the explicit rollout response permits local-only drafts.
  throw new ComposerRequestError(
    response.status === 404 && code === "mcp_disabled"
      ? code
      : code === "mcp_disabled"
        ? "request_failed"
        : code,
  );
}

export function personalComposerClient(
  workspaceId: string,
  planId: string,
): PersonalComposerClient {
  const path = `/api/work/workspaces/${encodeURIComponent(workspaceId)}/plans/${encodeURIComponent(planId)}/composer`;
  const write = async (method: "PUT" | "DELETE", input: SaveComposerFull | ClearPersonalComposer) =>
    PersonalComposerWriteSchema.parse(
      await responseBody(
        await fetch(path, {
          method,
          credentials: "same-origin",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(input),
        }),
      ),
    );
  return {
    read: async () =>
      PersonalComposerReadSchema.parse(
        await responseBody(await fetch(path, { credentials: "same-origin", cache: "no-store" })),
      ),
    save: (input) => write("PUT", input),
    clear: (input) => write("DELETE", input),
  };
}
